const CODE_MESSAGES = {
  INSUFFICIENT_BUYING_POWER: 'Not enough available cash to place this order.',
  INSUFFICIENT_FUNDS: 'Not enough available cash to place this order.',
  BUYING_POWER_EXCEEDED: 'Not enough available cash to place this order.',
  INSUFFICIENT_SHARES: 'This customer does not hold enough shares to sell.',
  INSUFFICIENT_POSITION: 'This customer does not hold enough shares to sell.',
  MARKET_CLOSED: 'The market is closed for this instrument.',
  MARKET_NOT_OPEN: 'The market is closed for this instrument.',
  INVALID_QUANTITY: 'Enter a share quantity greater than zero.',
  INVALID_PRICE: 'Enter a valid limit price.',
  ASSET_NOT_FOUND: 'That instrument could not be found.',
  ASSET_NOT_TRADABLE: 'This instrument cannot be traded right now.',
  CUSTOMER_NOT_FOUND: 'That customer was not found in this organization.',
  ORDER_REJECTED: 'The order was rejected.',
  VALIDATION_ERROR: 'Check the order details and try again.',
  IDEMPOTENCY_CONFLICT: 'This order was already submitted. Check the orders list before trying again.',
};

const STACK_PATTERN = /\bat\s+\S+\s+\(|\.js:\d+:\d+|node_modules|SyntaxError|TypeError|ReferenceError/;
const CODE_PATTERN = /^[A-Z0-9]+(?:[._-][A-Z0-9]+)+$/;

const asText = (value) => (typeof value === 'string' ? value.trim() : '');

const looksLikeStack = (text) => STACK_PATTERN.test(text) || text.includes('\n    at ');

const looksLikeCode = (text) => CODE_PATTERN.test(text) || (CODE_MESSAGES[text] != null && !text.includes(' '));

const collectMessages = (value, depth = 0) => {
  if (depth > 4 || value == null) return [];
  if (typeof value === 'string') return asText(value) ? [asText(value)] : [];
  if (Array.isArray(value)) return value.flatMap((item) => collectMessages(item, depth + 1));
  if (typeof value === 'object') {
    return [
      ...collectMessages(value.message, depth + 1),
      ...collectMessages(value.messages, depth + 1),
      ...collectMessages(value.errors, depth + 1),
      ...collectMessages(value.details, depth + 1),
    ];
  }
  return [];
};

const unique = (items) => {
  const seen = new Set();
  return items.filter((item) => {
    if (!item || seen.has(item)) return false;
    seen.add(item);
    return true;
  });
};

const humanize = (text) => {
  if (!text) return '';
  if (CODE_MESSAGES[text]) return CODE_MESSAGES[text];
  if (looksLikeCode(text)) return '';
  if (looksLikeStack(text)) return '';
  if (/^request failed with status code \d+$/i.test(text)) return '';
  return text;
};

const payloadOf = (error) => {
  const data = error?.response?.data;
  if (!data || typeof data !== 'object') return {};
  const nested = data.data && typeof data.data === 'object' && !Array.isArray(data.data) ? data.data : null;
  return nested ? { ...data, ...nested } : data;
};

export const orderErrorMessage = (error, fallback) => {
  const payload = payloadOf(error);
  const status = error?.response?.status;
  const reject = humanize(asText(payload.rejectReason) || asText(payload.reason));
  const coded = CODE_MESSAGES[asText(payload.errorCode)]
    || CODE_MESSAGES[asText(payload.code)]
    || CODE_MESSAGES[asText(payload.error)];
  const texts = unique([
    reject,
    coded,
    ...collectMessages(payload.message).map(humanize),
    ...collectMessages(payload.errors).map(humanize),
    ...collectMessages(payload.details).map(humanize),
  ].filter(Boolean));

  if (texts.length > 0) return texts.join(' ');

  if (status === 422) return 'The order was rejected. Review the quantity, price, and available cash.';
  if (status === 400) return 'Some order details are invalid. Review them and try again.';
  if (status === 403) return 'You do not have permission to place this order.';
  if (status === 404) return 'The customer or instrument could not be found.';
  if (status === 409) return CODE_MESSAGES.IDEMPOTENCY_CONFLICT;
  if (status === 429) return 'Too many order requests. Wait a moment and try again.';
  if (!error?.response) return 'Could not reach the order service. Check your connection and try again.';
  if (status >= 500) return 'The order service is unavailable. Try again in a moment.';
  return fallback;
};
